import { EventEmitter, Injectable, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CNConsultation, ContactServiceRule, ContactServiceRules } from '../models/cnconsultation';
import { CNPatient } from '../models/cnpatient';
import { CNServiceCode } from '../models/cnservicecode';
import { CNServiceCodeCustom } from '../models/cnservicecodecustom';
import { CNServiceCodeGroup } from '../models/cnservicecodegroup';
import { CNServiceCodeINAMI } from '../models/cnservicecodeinami';
import { CNServiceCodeINAMIValue } from '../models/cnservicecodeinamivalue';
import { SNItemFactory } from '../models/snitemfactory';
import { SNRestResponse } from '../models/snrestresponse';
import { SNUser } from '../models/snuser';
import { BecareService } from './becare.service';
import { PatientService } from './patient.service';
import { UserConfigService } from './user-config.service';

export enum ServicesServiceFilter {
  all = 'all'
}

export class ServicesServiceSubject {
  private becareService: BecareService;
  private myPatient: CNPatient;
  private myName: string;
  private myFilter: ServicesServiceFilter;
  private paged: boolean | number;
  private buffer: CNConsultation[];
  public consultations: BehaviorSubject<CNConsultation[]>;
  public myStart: number;
  public myLimit: number;
  public length: number;

  private servicesUrl = '';

  constructor(becare: BecareService, patient: CNPatient, name: string, filter: ServicesServiceFilter, paged: boolean | number) {
    this.becareService = becare;
    this.myName = name;
    this.myFilter = filter;
    this.paged = paged;
    if (paged) {
      this.myStart = 0;
      this.myLimit = 10;
      this.length = 0;
    }
    this.consultations = new BehaviorSubject<CNConsultation[]>([]);
    // Set patient, build rest url and query first buffer
    this.patient = patient;
  }

  private load() {
    if (!this.servicesUrl) {
      return;
    }
    this.becareService.call(this.servicesUrl).subscribe(result => {
      if (result.success === true && result.contacts !== undefined) {
        const factory = new SNItemFactory();
        this.buffer = factory.itemsFromJson(result.contacts);
        this.length = result.total;
        this.consultations.next(this.buffer);
      }
    });
  }

  private buildUrl() {
    if (this.paged) {
      this.servicesUrl = '/fr/services/account/patient/' + this.myPatient.id + '/contacts?start=' + this.myStart + '&limit=' + this.myLimit;
    } else {
      this.servicesUrl = '/fr/services/account/patient/' + this.myPatient.id + '/contacts';
    }
  }

  clear(): void {
    this.buffer = [];
    this.length = 0;
    this.consultations.next([]);
  }

  set patient(patient: CNPatient) {
    this.myPatient = patient;
    if (!this.myPatient) {
      return;
    }
    this.buildUrl();
    this.load();
  }

  set start(start: number) {
    this.myStart = start;
    this.buildUrl();
    this.load();
  }

  set limit(limit: number) {
    this.myLimit = limit;
    this.buildUrl();
    this.load();
  }
}

@Injectable({
  providedIn: 'root'
})
export class ServicesService {

  private static instance: ServicesService = null;
  private currentpatient: CNPatient;
  private codeGroups: CNServiceCodeGroup[];
  private user: SNUser;
  private subjects: Map<string, ServicesServiceSubject> = new Map();
  private contactRules: Map<string, ContactServiceRules> = new Map();

  public groups = new BehaviorSubject<CNServiceCodeGroup[]>([]);
  @Output() clear = new EventEmitter<boolean>();

  constructor(private becare: BecareService, private patient: PatientService, private userConfig: UserConfigService,
              private factory: SNItemFactory) {
    ServicesService.instance = this;
    // Initiate with current selected patient
    this.currentpatient = this.patient.patient;
    // Update subjects when patient selected
    this.patient.patientSelectedEvent.subscribe(patientselected => {
      this.currentpatient = patientselected;
      this.subjects.forEach((subject: ServicesServiceSubject, name: string) => {
        subject.patient = this.currentpatient;
      });
      this.clear.emit(true);
    });
    this.userConfig.configLoaded.subscribe(() => {
      this.user = this.userConfig.user;
      this.loadCodesGroups();
    });
  }

  public static getInstance(): ServicesService {
    return ServicesService.instance;
  }

  public getSubject(name: string, filter?: ServicesServiceFilter, paged?: boolean | number): ServicesServiceSubject {
    if (this.subjects.has(name)) {
      return this.subjects.get(name);
    }
    if (!filter && !paged) {
      return undefined;
    }
    // Build new service subject
    const newSubject = new ServicesServiceSubject(this.becare, this.currentpatient, name, filter, paged);
    this.subjects.set(name, newSubject);
    return newSubject;
  }

  public loadCodesGroups(user?: SNUser) {
    let userToUse = this.user;
    if (user) {
      userToUse = user;
    }
    if (userToUse) {
      this.becare.call('/fr/services/user/' + userToUse.id + '/groups/codes?withcodes=true').subscribe(result => {
        if (result.success === true && result.codegroups !== undefined) {
          const factory = new SNItemFactory();
          this.codeGroups = factory.itemsFromJson(result.codegroups);
          this.groups.next(this.codeGroups);
        }
      });
    }
  }

  public searchCode(query?: string): Observable<CNServiceCode[]> {
    return new Observable(observer => {
      const queryParam = (query) ? '?q=' + query : '';
      this.becare.call('/fr/services/codes' + queryParam).subscribe(result => {
        const response = new SNRestResponse(result);
        if (!response.valid || !response.success) {
          observer.error(response.message);
        } else {
          observer.next(this.factory.itemsFromJson(response.result('codes')));
        }
      });
    });
  }

  public addCode(code: CNServiceCode): Observable<CNServiceCodeCustom> {
    return new Observable(observer => {
      this.becare.call('/fr/services/codes', [], code, 'post').subscribe(result => {
        const response = new SNRestResponse(result);
        if (!response.valid || !response.success) {
          observer.error(response.message);
        } else {
          observer.next(this.factory.itemFromJson(response.result('code')));
        }
      });
    });
  }

  public updateCode(code: CNServiceCode): Observable<CNServiceCodeCustom> {
    return new Observable(observer => {
      this.becare.call('/fr/services/code/' + code.id, [], code, 'put').subscribe(result => {
        const response = new SNRestResponse(result);
        if (!response.valid || !response.success) {
          observer.error(response.message);
        } else {
          observer.next(this.factory.itemFromJson(response.result('code')));
        }
      });
    });
  }

  public deleteCode(code: CNServiceCode): Observable<boolean> {
    return new Observable(observer => {
      this.becare.call('/fr/services/code/' + code.id, [], code, 'delete').subscribe(result => {
        const response = new SNRestResponse(result);
        if (!response.valid || !response.success) {
          observer.error(response.message);
        } else {
          observer.next(true);
        }
      });
    });
  }

  public codeValues(code: CNServiceCode): Observable<CNServiceCode> {
    return new Observable(observer => {
      this.becare.call('/fr/services/code/' + code.id.toString() + '/values').subscribe(result => {
        const response = new SNRestResponse(result);
        if (!response.valid || !response.success) {
          observer.error(response.message);
        } else {
          observer.next(this.factory.itemFromJson(response.result('code')));
        }
      });
    });
  }

  public codeVersion(code: CNServiceCodeINAMI): Observable<any> {
    return new Observable(observer => {
      this.becare.call('/fr/services/code/' + code.id.toString() + '/versions').subscribe(result => {
        const response = new SNRestResponse(result);
        if (!response.valid || !response.success) {
          observer.error(response.message);
        } else {
          observer.next(response.result('codes'));
        }
      });
    });
  }

  public loadContactRules(type: string) {
    this.becare.call('/fr/services/contact/' + type.toLowerCase() + '/rules').subscribe(result => {
      const response = new SNRestResponse(result);
      if (!response.valid || !response.success) {
        console.log('Unable to load contact type rules', response.message);
      } else {
        const jsonRules = response.result('rules');
        const rules = new ContactServiceRules();
        Object.assign(rules, jsonRules);
        rules.code = [];
        if (typeof jsonRules.code !== undefined && Array.isArray(jsonRules.code)) {
          jsonRules.code.forEach(code => {
            const newCode = new ContactServiceRule();
            Object.assign(newCode, code);
            rules.code.push(newCode);
          });
        }
        this.contactRules.set(type, rules);
        console.log('add rule', rules);
      }
    });
  }

  public contactCodeRule(type: string): ContactServiceRules {
    if (!this.contactRules.has(type)) {
      return undefined;
    }
    return this.contactRules.get(type);
  }

  public updateValue(value: CNServiceCodeINAMIValue): Observable<CNServiceCodeINAMIValue> {
    return new Observable(observer => {
      this.becare.call('/fr/services/code/value/' + value.id, [], value, 'put').subscribe(result => {
        const response = new SNRestResponse(result);
        if (!response.valid || !response.success) {
          observer.error(response.message);
        } else {
          observer.next(this.factory.itemFromJson(response.result('value')));
        }
      });
    });
  }

}
